import React, { useEffect, useState } from "react";
import {
  Database, Users, RefreshCw, TrendingUp, Sparkles, ArrowRight,
  CheckCircle2, X, Zap, ChevronRight,
} from "lucide-react";

const STEP_ICON = {
  memory: Database,
  notify: Users,
  recalc: RefreshCw,
  trajectory: TrendingUp,
  impact: Sparkles,
  next: Zap,
};

const SEAT_LABEL = { cfo: "CFO", ceo: "CEO", cto: "CTO", cmo: "CMO" };

/**
 * ChainReactionModal — the "one decision starts a chain reaction" experience.
 * Plays the lifecycle steps with a staggered reveal, animates the strategic
 * progress bar from before→after, and surfaces the next recommendation.
 */
export const ChainReactionModal = ({ chain, onClose, onOpenNext }) => {
  const steps = chain?.steps || [];
  const [revealed, setRevealed] = useState(0);
  const [barPct, setBarPct] = useState(chain?.strategic_progress?.before ?? 0);

  useEffect(() => {
    if (!steps.length) return;
    setRevealed(0);
    const timers = steps.map((_, i) =>
      setTimeout(() => setRevealed((r) => Math.max(r, i + 1)), 350 * (i + 1))
    );
    return () => timers.forEach(clearTimeout);
  }, [steps.length]);

  useEffect(() => {
    const after = chain?.strategic_progress?.after ?? 0;
    const t = setTimeout(() => setBarPct(after), 900);
    return () => clearTimeout(t);
  }, [chain]);

  if (!chain) return null;

  const sp = chain.strategic_progress || {};
  const next = chain.next_recommendation;
  const delta = sp.delta || 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/50 backdrop-blur-sm px-4"
      data-testid="chain-reaction-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-ink-400 hover:text-ink-600"
          data-testid="chain-reaction-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-status-onTrack-text">
            <Zap className="h-3.5 w-3.5" /> Chain reaction triggered
          </div>
          <h2 className="mt-1 text-[18px] font-display font-bold text-blue-800" data-testid="chain-reaction-title">
            {chain.acted?.title}
          </h2>
          <p className="mt-0.5 text-[12.5px] text-ink-500">
            {chain.acted?.owner_title} decision approved — here's how it moved the business.
          </p>
        </div>

        {/* Steps */}
        <div className="px-6 py-4 space-y-2.5 max-h-[42vh] overflow-y-auto" data-testid="chain-reaction-steps">
          {steps.map((s, i) => {
            const Icon = STEP_ICON[s.key] || CheckCircle2;
            const on = i < revealed;
            return (
              <div
                key={s.key}
                data-testid={`chain-step-${s.key}`}
                className={`flex items-start gap-3 rounded-xl border p-3 transition-all duration-500 ${
                  on
                    ? "border-status-onTrack-border bg-status-onTrack-bg/60 opacity-100 translate-y-0"
                    : "border-ink-100 bg-white opacity-40 translate-y-1"
                }`}
              >
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  on ? "bg-status-onTrack-bg text-status-onTrack-text" : "bg-ink-100 text-ink-400"
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-blue-800">{s.label}</span>
                    {on && <CheckCircle2 className="h-3.5 w-3.5 text-status-onTrack" />}
                  </div>
                  <p className="text-[12.5px] text-ink-600 leading-snug">{s.detail}</p>
                  {s.key === "notify" && (s.seats?.length > 0) && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {s.seats.map((seat) => (
                        <span key={seat}
                          className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md bg-white border border-status-onTrack-border text-status-onTrack-text">
                          {SEAT_LABEL[seat] || seat.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Strategic progress bar */}
        <div className="px-6 pb-2" data-testid="chain-progress">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-ink-400 mb-1.5">
            <span>Strategic progress{sp.target_label ? ` · ${sp.target_label}` : ""}</span>
            <span className="text-[12px] font-bold tabular-nums text-status-onTrack-text" data-testid="chain-progress-pct">
              {sp.after ?? 0}%{delta > 0 ? ` (+${delta})` : ""}
            </span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-ink-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-status-onTrack transition-all duration-[1200ms] ease-out"
              style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-ink-500">
            {sp.addressed ?? 0} of {sp.total ?? 0} executive priorities addressed.
          </div>
        </div>

        {/* Next recommendation */}
        <div className="px-6 pb-6 pt-3">
          {next ? (
            <button
              onClick={() => onOpenNext?.(next)}
              data-testid="chain-next-recommendation"
              className="w-full text-left rounded-xl border border-blue-800/20 bg-ink-50 p-3.5 hover:bg-ink-100 transition-colors group"
            >
              <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">
                <Sparkles className="h-3 w-3" /> Next highest-priority recommendation
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="text-[13.5px] font-bold text-blue-800">{next.title}</span>
                <ChevronRight className="h-4 w-4 text-ink-400 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="mt-0.5 text-[12px] text-ink-500 line-clamp-2">{next.rationale}</p>
            </button>
          ) : (
            <div className="rounded-xl border border-status-onTrack-border bg-status-onTrack-bg p-3.5 flex items-center gap-2"
                 data-testid="chain-next-clear">
              <CheckCircle2 className="h-4 w-4 text-status-onTrack" />
              <span className="text-[13px] text-status-onTrack-text font-medium">
                Queue clear — no cross-functional decision needs attention right now.
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            data-testid="chain-reaction-done"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-800 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-900"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChainReactionModal;
